import { createElement as rc, useEffect, useState, useCallback } from 'react';
import styled from '../../styled';
import fromTheme from '../../fromTheme';
import Input from '../TextInput';
import Button from '../Button';
import colors from '../../styling/themes/gl2025/colors';

const InnerContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${fromTheme('datepicker', 'timepicker', 'innerContainerGap')};
`;

const OuterContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${fromTheme('datepicker', 'timepicker', 'outerContainerGap')};
`;

const HourMinuteInput = styled(Input)`
    width: ${fromTheme('datepicker', 'timepicker', 'hourMinuteWidth')} !important;

    // Remove spinner for Firefox
    -moz-appearance: textfield;
    appearance: none;

    // Remove spinner for other browsers
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const Colon = styled.span`
    ${fromTheme('datepicker', 'timepicker', 'colonFont')};
    color: ${fromTheme('datepicker', 'timepicker', 'colonFontColor')};
    padding-bottom: ${fromTheme('datepicker', 'timepicker', 'colonBottomPadding')};
`;

const AmButton = styled(Button)`
    border-radius: ${fromTheme('datepicker', 'timepicker', 'amButtonRadius')};
    margin-right: 0;
    margin-bottom: ${fromTheme('datepicker', 'timepicker', 'amPmButtonMarginBottom')};
    box-shadow: ${({ selected }) => selected ? 'unset' : fromTheme('datepicker', 'timepicker', 'amPmButtonBoxShadow')};

    & > span {
        box-shadow: none;
        font-weight: ${fromTheme('datepicker', 'timepicker', 'amPmFontWeight')};
    }

    &:hover {
        border-radius: ${fromTheme('datepicker', 'timepicker', 'amButtonRadius')} !important;
        margin-right: 0 !important;
        margin-bottom: ${fromTheme('datepicker', 'timepicker', 'amPmButtonMarginBottom')} !important;
        box-shadow: ${({ selected }) => selected ? 'unset' : `0 0 0 1px ${colors.coolGray300} inset`} !important;
        cursor: ${({ selected }) => selected ? 'default' : 'pointer' } !important;

        & > span {
            box-shadow: none !important;
            font-weight: ${fromTheme('datepicker', 'timepicker', 'amPmFontWeight')} !important;
        }
    }

    &:focus {
        border-radius: ${fromTheme('datepicker', 'timepicker', 'amButtonRadius')} !important;
        margin-right: 0 !important;
        margin-bottom: ${fromTheme('datepicker', 'timepicker', 'amPmButtonMarginBottom')} !important;
        box-shadow: ${({ selected }) => selected ? 'unset' : fromTheme('datepicker', 'timepicker', 'amPmButtonBoxShadow')} !important;

        & > span {
            box-shadow: none !important;
            font-weight: ${fromTheme('datepicker', 'timepicker', 'amPmFontWeight')} !important;
        }

    }
`;

const PmButton = styled(Button)`
    border-radius: ${fromTheme('datepicker', 'timepicker', 'pmButtonRadius')};
    margin-left: 0;
    margin-bottom: ${fromTheme('datepicker', 'timepicker', 'amPmButtonMarginBottom')};
    box-shadow: ${({ selected }) => selected ? 'unset' : fromTheme('datepicker', 'timepicker', 'amPmButtonBoxShadow')};


    & > span {
        box-shadow: none;
        font-weight: ${fromTheme('datepicker', 'timepicker', 'amPmFontWeight')};
    }

    &:hover {
        border-radius: ${fromTheme('datepicker', 'timepicker', 'pmButtonRadius')} !important;
        margin-left: 0 !important;
        margin-bottom: ${fromTheme('datepicker', 'timepicker', 'amPmButtonMarginBottom')} !important;
        box-shadow: ${({ selected }) => selected ? 'unset' : `0 0 0 1px ${colors.coolGray300} inset`} !important;
        cursor: ${({ selected }) => selected ? 'default' : 'pointer' };

        & > span {
            box-shadow: none !important;
            font-weight: ${fromTheme('datepicker', 'timepicker', 'amPmFontWeight')} !important;
        }

    }

    &:focus {
        border-radius: ${fromTheme('datepicker', 'timepicker', 'pmButtonRadius')} !important;
        margin-left: 0 !important;
        margin-bottom: ${fromTheme('datepicker', 'timepicker', 'amPmButtonMarginBottom')} !important;
        box-shadow: ${({ selected }) => selected ? 'unset' : fromTheme('datepicker', 'timepicker', 'amPmButtonBoxShadow')} !important;

        & > span {
            box-shadow: none !important;
            font-weight: ${fromTheme('datepicker', 'timepicker', 'amPmFontWeight')} !important;
        }
    }
`;

const AmPmContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

const AmPmToggle = props => {

    const { defaultValue, onChange } = props;

    const [selected, setSelected] = useState(defaultValue);

    const onClick = (value) => {
        setSelected(value);
        onChange(value);
    };

    return rc(AmPmContainer, { },
        rc(AmButton, {
            text: 'AM',
            variant: selected === 'AM' ? 'primary' : 'secondary',
            selected: selected === 'AM',
            onClick: () => onClick('AM')
        }),
        rc(PmButton, {
            text: 'PM',
            variant: selected === 'PM' ? 'primary' : 'secondary',
            selected: selected === 'PM',
            onClick: () => onClick('PM')
        })
    );

};

const TimePicker = props => {

    const { defaultHourValue, defaultMinuteValue, defaultAmPmValue, onChange } = props;

    const [hourValue, setHourValue] = useState(defaultHourValue ?? '');
    const [minuteValue, setMinuteValue] = useState((defaultMinuteValue < 10 ? '0' + defaultMinuteValue : defaultMinuteValue) ?? '');
    const [amPmValue, setAmPmValue] = useState(defaultAmPmValue ?? 'AM');

    const [hourError, setHourError] = useState(false);
    const [minuteError, setMinuteError] = useState(false);

    const validateHour = useCallback(() => {
        if (parseInt(hourValue) < 1 || parseInt(hourValue) > 12) {
            setHourError(true);
            return false;
        } else {
            setHourError(false);
            return true;
        }
    }, [hourValue]);

    const validateMinute = useCallback(() => {
        if (parseInt(minuteValue) < 0 || parseInt(minuteValue) > 59) {
            setMinuteError(true);
            return false;
        } else {
            setMinuteError(false);
            return true;
        }
    }, [minuteValue]);

    const updateTime = useCallback(() => {
        if (validateHour() && validateMinute()) {
            const hour = parseInt(hourValue) === 12 ? 0 : parseInt(hourValue);
            const newTime = `${amPmValue === 'PM' ? 12 + hour : hour}:${minuteValue.length < 2 ? '0' + minuteValue : minuteValue}`;
            onChange(newTime);
        }
    }, [amPmValue, hourValue, minuteValue, onChange, validateHour, validateMinute]);

    useEffect(() => {
        updateTime();
    }, [updateTime]);

    return rc(OuterContainer, { onBlur: updateTime },
        rc(InnerContainer, { },
            rc(HourMinuteInput, {
                label: 'Time',
                sequence: 1,
                placeholder: 'HH',
                value: hourValue,
                onChange: setHourValue,
                type: 'number',
                maxLength: 2,
                onBlur: updateTime,
                destructive: hourError
            }),
            rc(Colon, {}, ':'),
            rc(HourMinuteInput, {
                sequence: 2,
                placeholder: 'MM',
                value: minuteValue,
                onChange: setMinuteValue,
                type: 'number',
                maxLength: 2,
                onBlur: updateTime,
                destructive: minuteError
            })
        ),
        rc(AmPmToggle, { defaultValue: amPmValue, onChange: setAmPmValue })
    );
};

export default TimePicker;